import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { PublicShell } from "@/components/PublicShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, ShieldCheck, Loader2, ArrowLeft } from "lucide-react";

function Field({ f, value, setValue }) {
  const common = { "data-testid": `field-${f.key}` };
  if (["text", "email", "tel", "date", "number"].includes(f.type)) {
    return <Input {...common} type={f.type} placeholder={f.placeholder || ""} value={value || ""} onChange={(e) => setValue(e.target.value)} />;
  }
  if (f.type === "textarea") {
    return <Textarea {...common} placeholder={f.placeholder || ""} value={value || ""} onChange={(e) => setValue(e.target.value)} className="min-h-[80px]" />;
  }
  if (f.type === "select") {
    return (
      <Select value={value || ""} onValueChange={setValue}>
        <SelectTrigger {...common}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent className="bg-popover">
          {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === "radio") {
    return (
      <div className="flex flex-wrap gap-2" {...common}>
        {f.options.map((o) => (
          <button key={o} type="button" data-testid={`opt-${f.key}-${o}`} onClick={() => setValue(o)}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${value === o ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (f.type === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => setValue(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div className="flex flex-wrap gap-2" {...common}>
        {f.options.map((o) => (
          <button key={o} type="button" data-testid={`opt-${f.key}-${o}`} onClick={() => toggle(o)}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${arr.includes(o) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (f.type === "boolean") {
    return (
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-secondary/40 p-3 text-sm">
        <Checkbox {...common} checked={value === true} onCheckedChange={(c) => setValue(!!c)} className="mt-0.5" />
        <span>{f.label}</span>
      </label>
    );
  }
  return null;
}

export default function PublicForm({ templateKey }) {
  const params = useParams();
  const key = templateKey || params.key;
  const [sp] = useSearchParams();
  const practiceQ = sp.get("p");

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [values, setValues] = useState({});
  const [missing, setMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/public/forms/${key}${practiceQ ? `?p=${practiceQ}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This form is not available."));
  }, [key, practiceQ]);

  const set = (k) => (v) => setValues((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setMissing([]);
    setSubmitting(true);
    try {
      const { data: res } = await api.post(
        `/public/forms/${key}/submit${practiceQ ? `?p=${practiceQ}` : ""}`,
        { responses: values, submitted_by_email: values.email, submitted_by_phone: values.phone },
      );
      setDone(res.message);
      window.scrollTo(0, 0);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      if (d && typeof d === "object" && d.fields) setMissing(d.fields);
      setErr(typeof d === "string" ? d : d?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (err && !data) {
    return (
      <PublicShell>
        <Card className="mx-auto max-w-md rounded-2xl p-8 text-center">
          <p className="text-sm text-muted-foreground">{err}</p>
          <Link to="/forms"><Button variant="outline" className="mt-4 rounded-full">See available forms</Button></Link>
        </Card>
      </PublicShell>
    );
  }
  if (!data) return <PublicShell><div className="py-20 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div></PublicShell>;

  if (done) {
    return (
      <PublicShell practiceName={data.practice?.name}>
        <Card className="mx-auto max-w-md rounded-2xl p-10 text-center animate-rise" data-testid="form-success">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h1 className="text-xl font-bold tracking-tight">All done</h1>
          <p className="mt-2 text-sm text-muted-foreground">{done}</p>
        </Card>
      </PublicShell>
    );
  }

  const { form } = data;
  return (
    <PublicShell practiceName={data.practice?.name}>
      <div className="mx-auto max-w-2xl animate-rise">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{form.title}</h1>
        {form.description && <p className="mt-2 text-sm text-muted-foreground">{form.description}</p>}

        {missing.length > 0 && (
          <div className="mt-4 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" data-testid="form-missing">
            Please complete: {missing.join(", ")}
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-8">
          {/* honeypot */}
          <input tabIndex={-1} autoComplete="off" aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }}
            value={values._hp || ""} onChange={(e) => setValues({ ...values, _hp: e.target.value })} />

          {form.sections.map((sec, si) => (
            <Card key={si} className="rounded-2xl border-border/60 p-6">
              <h2 className="text-base font-semibold">{sec.title}</h2>
              <div className="mt-4 space-y-4">
                {sec.fields.map((f) => (
                  <div key={f.key}>
                    {f.type !== "boolean" && (
                      <Label className="mb-1.5 block">{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
                    )}
                    <Field f={f} value={values[f.key]} setValue={set(f.key)} />
                  </div>
                ))}
              </div>
            </Card>
          ))}

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" /> Secured & POPIA-compliant
            </div>
            <Button type="submit" data-testid="form-submit" disabled={submitting} className="rounded-full px-8">
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </PublicShell>
  );
}
